/**
 * Custom Ad Banner Component
 * รองรับรูปโฆษณาขนาดกำหนดเอง (1000x650 และ 1000x400)
 */

import { useState, useEffect } from 'react';

interface CustomAdBannerProps {
  /** ประเภทโฆษณา */
  type: 'hero' | 'sidebar' | 'in-feed' | 'top-banner' | 'company-header';

  /** URL รูปภาพ */
  imageUrl: string;

  /** ลิงก์ปลายทาง */
  targetUrl: string;

  /** Alt text สำหรับ SEO */
  alt?: string;

  /** Custom className */
  className?: string;

  /** Sticky sidebar */
  sticky?: boolean;
  stickyOffset?: number;

  /** Lazy loading */
  lazy?: boolean;

  /** Show "โฆษณา" label */
  showLabel?: boolean;
}

export default function CustomAdBanner({
  type,
  imageUrl,
  targetUrl,
  alt = 'โฆษณา',
  className = '',
  sticky = false,
  stickyOffset = 80,
  lazy = true,
  showLabel = true,
}: CustomAdBannerProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(!lazy);

  // Lazy loading observer
  useEffect(() => {
    if (!lazy) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { rootMargin: '200px' }
    );

    const element = document.getElementById(`ad-${type}`);
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [lazy, type]);

  // Get responsive sizes based on type
  const getResponsiveSizes = () => {
    switch (type) {
      case 'hero':
      case 'top-banner':
      case 'company-header':
        // 1000x400 - Widescreen Banner
        return {
          container: 'w-full max-w-7xl mx-auto',
          desktop: 'aspect-[10/4]', // 1000x400
          tablet: 'aspect-[10/4]',
          mobile: 'aspect-[5/2]', // 375x150
        };

      case 'sidebar':
        // 1000x650 - Sidebar
        return {
          container: 'w-full max-w-sm',
          desktop: 'aspect-[10/6.5]', // 1000x650 = ~400x260
          tablet: 'aspect-[10/6.5]',
          mobile: 'hidden', // ซ่อนบน mobile
        };

      case 'in-feed':
        // 1000x650 - In-Feed Highlight
        return {
          container: 'w-full',
          desktop: 'aspect-[10/6.5]', // 600x390
          tablet: 'aspect-[10/6.5]',
          mobile: 'aspect-[10/6.5]', // 350x227
        };

      default:
        return {
          container: 'w-full',
          desktop: 'aspect-[10/4]',
          tablet: 'aspect-[10/4]',
          mobile: 'aspect-[10/4]',
        };
    }
  };

  const sizes = getResponsiveSizes();

  // Click tracking
  const handleClick = () => {
    // Track ad click (Google Analytics, etc.)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'ad_click', {
        ad_type: type,
        ad_url: targetUrl,
      });
    }
  };

  return (
    <div
      id={`ad-${type}`}
      className={`
        ad-banner
        ${sizes.container}
        ${sticky ? 'sticky' : ''}
        ${className}
      `}
      style={sticky ? { top: `${stickyOffset}px` } : {}}
    >
      {/* Ad Label */}
      {showLabel && (
        <div className="text-xs text-gray-500 text-center mb-1">
          โฆษณา
        </div>
      )}

      {/* Ad Container */}
      <a
        href={targetUrl}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={handleClick}
        className="block overflow-hidden rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
      >
        {isVisible ? (
          <img
            src={imageUrl}
            alt={alt}
            className={`
              w-full h-auto object-contain
              ${isLoaded ? 'opacity-100' : 'opacity-0'}
              transition-opacity duration-300
            `}
            loading={lazy ? 'lazy' : 'eager'}
            onLoad={() => setIsLoaded(true)}
          />
        ) : (
          // Placeholder while lazy loading
          <div
            className={`
              w-full bg-gray-200 animate-pulse
              ${sizes.desktop}
              ${sizes.tablet}
              ${sizes.mobile}
            `}
          />
        )}
      </a>
    </div>
  );
}
